import React from 'react';
import styled from 'styled-components';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { heading_l, heading_s, heading_m, text_xscap } from '_utils/styles/text';
import { spacing } from '_utils/props';
import { mq, svg, plainCard, restrictCardTitle } from '_utils/styles/';
import { Link } from 'react-router-dom';

export const Container = styled.div`
  width: 100%;
  position: relative;
`;

export const ItemsListContainer = styled.div`
  display: flex;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 16px;
`;

export const CarouselItem = styled.div`
  flex: none;
  width: 224px;
  display: flex;
  flex-direction: column;

  ${mq('md')} {
    width: 288px;
  }

  .flickity-resize & {
    top: 0;
    bottom: 0;
  }

  & + & {
    ${({ theme }) => spacing({ theme, $ml: 4, $mt: 0 })}
  }
`;

// Need to remove extra props getting sent to the dom
// https://github.com/styled-components/styled-components/issues/135
export const Card = styled(({ noticeEnabled, noticeLevel, noticeMessage, to, ...rest }) => (
  <Link to={to} {...rest} />
))`
  ${({ theme }) => plainCard({ theme })}
  width: 100%;
  display: flex;

  transition: ${({ theme: { motion } }) => `border ${motion.timing.default} ${motion.ease}`};
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
  color: inherit;
  text-decoration: none;

  position: relative;

  &:after {
    content: '';
    position: absolute;
    width: 92%;
    height: 90%;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    box-shadow: ${({ theme }) => theme.card.boxShadow};
    transition: ${({ theme: { motion } }) => `box-shadow ${motion.timing.default} ${motion.ease}`};

    z-index: -1;
  }

  &:hover {
    border: ${({ theme }) => theme.card.borderHover};

    &:after {
      box-shadow: ${({ theme }) => theme.card.boxShadowHover};
    }
  }
`;

export const CardHeader = styled.span`
  display: flex;
  flex: none;
  flex-direction: column;
`;

export const CardFooter = styled.span`
  display: flex;
  flex: none;
  flex-direction: column;
`;

export const CardSection = styled.span`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${({ theme }) => spacing({ theme, $p: 3 })}

  .image & {
    justify-content: flex-start;
  }
`;

export const ImageWrapper = styled.span`
  .image & {
    width: 100%;
    display: block;
    position: relative;
    overflow: hidden;
    border-top-left-radius: ${({ theme }) => theme.card.borderRadius - 2}px;
    border-top-right-radius: ${({ theme }) => theme.card.borderRadius - 2}px;

    &:before {
      content: '';
      display: block;
      padding-bottom: 75%;
    }
  }

  .publication &,
  .focus & {
    background-color: ${({ theme }) => theme.colors.background.tertiary};
  }

  .no-image & {
    display: flex;
    height: 100%;
    align-items: flex-end;
    background-color: transparent;
    ${({ theme }) => spacing({ theme, $px: 3, $pt: 3 })}
    &:before {
      content: '';
      display: block;
      height: 14px;
    }
  }
`;

export const ImageContainer = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    font-family: 'object-fit: cover;';
  }

  .publication & {
    /* width 75% to make container square (inside 4:3 parent) */
    width: calc(75% - 32px);
    height: calc(100% - 32px);
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${({ theme }) => theme.colors.dna.default};
      border-radius: 50%;
      @media print {
        background-color: transparent;
      }
    }

    img {
      object-fit: contain;
      font-family: 'object-fit: contain;';
    }
  }

  .no-image & {
    display: none;
  }

  .focus & {
    width: calc(100% - 64px);
    height: calc(100% - 48px);
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);

    img {
      object-fit: contain;
      font-family: 'object-fit: contain;';
    }
  }
`;

export const Category = styled.span`
  display: block;
  ${text_xscap}
  margin: 0;
  ${({ theme }) => spacing({ theme, $mb: 1 })}
  color: ${({ theme }) => theme.card.secondary};
`;

export const Title = styled(Text)`
  ${restrictCardTitle}

  ${Card}:hover & {
    color: ${({ theme }) => theme.colors.element.tertiary};
    text-decoration: underline;
  }

  ${heading_s}

  .no-image & {
    ${heading_l}
  }

  ${mq('md')} {
    ${heading_m}

    .no-image & {
      ${heading_l}
    }
  }
`;

export const AttributesWrapper = styled.span`
  margin: 8px 0;
  display: block;
`;

export const AttributesIcon = styled.span`
  align-self: flex-start;
  flex: none;
`;

export const LinkIconContainer = styled.span`
  display: flex;
  height: 100%;
  align-items: flex-end;
  ${({ theme }) => spacing({ theme, $px: 3, $pb: 3 })}
`;

export const ArrowWrapper = styled.span`
  ${svg(14, 14)}
  width: 14px;

  ${mq('md')} {
    width: 19px;
  }

  path {
    fill: ${({ theme }) => theme.card.action};

    ${Card}:hover & {
      fill: ${({ theme }) => theme.card.actionHover};
    }
  }
`;
